import { useEffect, useState, useCallback } from "react";
import API_URL from "../config";

export default function WashBrosUsers() {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loadingId, setLoadingId] = useState(null); // id du user en cours de traitement
  const token = localStorage.getItem("token");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/auth/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erreur lors du chargement des utilisateurs");
      const data = await res.json();
      // Afficher en priorité les users WashBros actifs
      const sorted = [...data].sort((a, b) => {
        if (a.washBrosActif && !b.washBrosActif) return -1;
        if (!a.washBrosActif && b.washBrosActif) return 1;
        return 0;
      });
      setUsers(sorted);
      setFiltered(sorted);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Filtrage en temps réel
  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      users.filter(
        (u) =>
          u.username?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.nom?.toLowerCase().includes(q) ||
          u.prenom?.toLowerCase().includes(q)
      )
    );
  }, [search, users]);

  const confirmerPaiement = async (userId) => {
    setLoadingId(userId);
    try {
      const res = await fetch(`${API_URL}/api/auth/admin/users/${userId}/confirmer-paiement`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erreur confirmation");
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, washBrosPaye: true } : u))
      );
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setLoadingId(null);
    }
  };

  const annulerPaiement = async (userId) => {
    if (!window.confirm("Annuler le paiement de cet utilisateur ?")) return;
    setLoadingId(userId);
    try {
      const res = await fetch(`${API_URL}/api/auth/admin/users/${userId}/annuler-paiement`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erreur annulation");
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? { ...u, washBrosPaye: false } : u))
      );
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setLoadingId(null);
    }
  };

  const washBrosUsers = filtered.filter((u) => u.washBrosActif);
  const autresUsers = filtered.filter((u) => !u.washBrosActif);

  return (
    <div>
      <h2 className="text-2xl font-semibold text-blue-600 mb-1">
        🧺 Gestion Wash Bros
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Confirmez les paiements pour activer les QR codes des clients.
      </p>

      {/* Barre de recherche */}
      <div className="relative mb-5">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🔍</span>
        <input
          type="text"
          placeholder="Rechercher par nom, prénom, username ou email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
          >
            ✕
          </button>
        )}
      </div>

      {loading && (
        <p className="text-center text-gray-400 py-8">Chargement des utilisateurs...</p>
      )}
      {error && (
        <p className="text-center text-red-500 py-4">{error}</p>
      )}

      {!loading && !error && (
        <>
          {/* Stats rapides */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{users.length}</p>
              <p className="text-xs text-gray-500 mt-1">Total users</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-yellow-600">
                {users.filter((u) => u.washBrosActif && !u.washBrosPaye).length}
              </p>
              <p className="text-xs text-gray-500 mt-1">En attente</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-green-600">
                {users.filter((u) => u.washBrosPaye).length}
              </p>
              <p className="text-xs text-gray-500 mt-1">QR actifs</p>
            </div>
          </div>

          {/* Section abonnés Wash Bros */}
          {washBrosUsers.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Abonnés Wash Bros ({washBrosUsers.length})
              </h3>
              <div className="space-y-3">
                {washBrosUsers.map((u) => (
                  <UserCard
                    key={u._id}
                    user={u}
                    onConfirmer={confirmerPaiement}
                    onAnnuler={annulerPaiement}
                    isLoading={loadingId === u._id}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Séparateur si les deux sections existent */}
          {washBrosUsers.length > 0 && autresUsers.length > 0 && (
            <hr className="my-4 border-gray-100" />
          )}

          {/* Autres users */}
          {autresUsers.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
                Autres utilisateurs ({autresUsers.length})
              </h3>
              <div className="space-y-2">
                {autresUsers.map((u) => (
                  <UserCard
                    key={u._id}
                    user={u}
                    onConfirmer={confirmerPaiement}
                    onAnnuler={annulerPaiement}
                    isLoading={loadingId === u._id}
                    compact
                  />
                ))}
              </div>
            </div>
          )}

          {filtered.length === 0 && (
            <p className="text-center text-gray-400 py-8">Aucun utilisateur trouvé.</p>
          )}
        </>
      )}
    </div>
  );
}

function UserCard({ user, onConfirmer, onAnnuler, isLoading, compact = false }) {
  const initiales = ((user.prenom?.[0] || "") + (user.nom?.[0] || "")).toUpperCase() || user.username?.[0]?.toUpperCase() || "?";

  const statutLabel = !user.washBrosActif
    ? null
    : user.washBrosPaye
    ? { text: "QR Actif ✅", cls: "bg-green-100 text-green-700" }
    : { text: "⏳ En attente", cls: "bg-yellow-100 text-yellow-700" };

  if (compact) {
    return (
      <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500">
            {initiales}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">{user.username}</p>
            <p className="text-xs text-gray-400">{user.email}</p>
          </div>
        </div>
        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">{user.abonnement || "Gratuit"}</span>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-4 transition-all ${
      user.washBrosPaye
        ? "border-green-200 bg-green-50"
        : user.washBrosActif
        ? "border-yellow-200 bg-yellow-50"
        : "border-gray-100 bg-white"
    }`}>
      <div className="flex items-start justify-between gap-3">
        {/* Avatar + infos */}
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ${
            user.washBrosPaye ? "bg-green-500" : user.washBrosActif ? "bg-yellow-400" : "bg-gray-300"
          }`}>
            {initiales}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-gray-800 text-sm">{user.prenom} {user.nom}</p>
              <span className="text-xs text-gray-400">@{user.username}</span>
            </div>
            <p className="text-xs text-gray-500">{user.email}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                {user.abonnement || "Gratuit"}
              </span>
              {statutLabel && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statutLabel.cls}`}>
                  {statutLabel.text}
                </span>
              )}
              <span className="text-xs text-gray-400">{user.passages} passage{user.passages !== 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        {user.washBrosActif && (
          <div className="flex flex-col gap-2 flex-shrink-0">
            {!user.washBrosPaye ? (
              <button
                onClick={() => onConfirmer(user._id)}
                disabled={isLoading}
                className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded-lg transition disabled:opacity-50"
              >
                {isLoading ? "..." : "✅ Valider paiement"}
              </button>
            ) : (
              <button
                onClick={() => onAnnuler(user._id)}
                disabled={isLoading}
                className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-600 text-xs font-medium rounded-lg transition disabled:opacity-50"
              >
                {isLoading ? "..." : "Annuler"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
